/**
 * Role Hierarchy Visualization - Main Container Component
 *
 * FERP Principles:
 * - Data-driven: All hierarchy data from backend via repository
 * - No hardcoded business logic
 * - Complete type safety
 * - Modular architecture with separate view components
 *
 * GitHub Issue: #340
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  Stack,
  Title,
  Text,
  Group,
  Button,
  Badge,
  Alert,
  Tabs,
  Loader,
  Box,
} from '@mantine/core';
import {
  IconHierarchy,
  IconRefresh,
  IconX,
  IconAlertTriangle,
  IconShield,
  IconNetwork,
} from '@tabler/icons-react';
import {
  createHierarchyRepository,
  type RoleHierarchy,
} from '../../data/repositories/HierarchyRepository';
import { HierarchyTreeView } from './HierarchyTreeView';
import { HierarchyDiagramView } from './HierarchyDiagramView';
import { PermissionFlowView } from './PermissionFlowView';
import { HierarchyValidationPanel } from './HierarchyValidationPanel';

export interface RoleHierarchyVisualizationProps {
  tenantId: string;
  currentUser: string;
}

export function RoleHierarchyVisualization({
  tenantId,
  currentUser,
}: RoleHierarchyVisualizationProps) {
  const repository = useMemo(
    () => createHierarchyRepository(),
    []
  );

  const [hierarchy, setHierarchy] = useState<RoleHierarchy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('tree');

  // Load hierarchy
  const loadHierarchy = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) setLoading(true);
        setError(null);
        const data = await repository.getHierarchy(tenantId);
        setHierarchy(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hierarchy');
      } finally {
        setLoading(false);
      }
    },
    [repository, tenantId]
  );

  useEffect(() => {
    loadHierarchy();
  }, [loadHierarchy]);

  if (loading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack ta="center" gap="md" style={{ minHeight: 400 }}>
          <Loader size="xl" />
          <Text>Loading hierarchy...</Text>
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Alert icon={<IconX size={16} />} color="red" variant="light">
          <Text fw={500}>Error Loading Hierarchy</Text>
          <Text size="sm">{error}</Text>
          <Button
            size="xs"
            variant="light"
            mt="md"
            leftSection={<IconRefresh size={14} />}
            onClick={() => loadHierarchy()}
          >
            Retry
          </Button>
        </Alert>
      </Card>
    );
  }

  if (!hierarchy) {
    return null;
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>Role Hierarchy Visualization</Title>
            <Text size="sm" c="dimmed">
              Interactive visualization of role inheritance and permissions
            </Text>
          </div>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => loadHierarchy()}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats */}
        <Group gap="lg">
          <Box>
            <Text size="sm" c="dimmed">
              Total Roles
            </Text>
            <Text size="xl" fw={700}>
              {hierarchy.roles.length}
            </Text>
          </Box>
          <Box>
            <Text size="sm" c="dimmed">
              Root Roles
            </Text>
            <Text size="xl" fw={700}>
              {hierarchy.rootRoleIds.length}
            </Text>
          </Box>
          <Box>
            <Text size="sm" c="dimmed">
              Max Depth
            </Text>
            <Text size="xl" fw={700}>
              {hierarchy.maxDepth}
            </Text>
          </Box>
          <Box>
            <Text size="sm" c="dimmed">
              Issues
            </Text>
            <Text
              size="xl"
              fw={700}
              c={hierarchy.validationIssues.length > 0 ? 'red' : 'green'}
            >
              {hierarchy.validationIssues.length}
            </Text>
          </Box>
        </Group>

        {/* Tabs */}
        <Tabs value={selectedTab} onChange={(val) => setSelectedTab(val || 'tree')}>
          <Tabs.List>
            <Tabs.Tab value="tree" leftSection={<IconHierarchy size={16} />}>
              Tree View
            </Tabs.Tab>
            <Tabs.Tab value="diagram" leftSection={<IconNetwork size={16} />}>
              Interactive Diagram
            </Tabs.Tab>
            <Tabs.Tab value="validation" leftSection={<IconAlertTriangle size={16} />}>
              Validation ({hierarchy.validationIssues.length})
            </Tabs.Tab>
            <Tabs.Tab value="permissions" leftSection={<IconShield size={16} />}>
              Permission Flow
            </Tabs.Tab>
          </Tabs.List>

          {/* Tree View */}
          <Tabs.Panel value="tree" pt="md">
            <HierarchyTreeView
              hierarchy={hierarchy}
              tenantId={tenantId}
              currentUser={currentUser}
              onHierarchyChange={() => loadHierarchy(false)}
            />
          </Tabs.Panel>

          {/* Interactive Diagram */}
          <Tabs.Panel value="diagram" pt="md">
            <HierarchyDiagramView
              hierarchy={hierarchy}
              tenantId={tenantId}
              currentUser={currentUser}
              onHierarchyChange={() => loadHierarchy(false)}
            />
          </Tabs.Panel>

          {/* Validation */}
          <Tabs.Panel value="validation" pt="md">
            <HierarchyValidationPanel
              hierarchy={hierarchy}
              tenantId={tenantId}
              onFixIssue={() => loadHierarchy(false)}
            />
          </Tabs.Panel>

          {/* Permission Flow */}
          <Tabs.Panel value="permissions" pt="md">
            <PermissionFlowView
              hierarchy={hierarchy}
              tenantId={tenantId}
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Card>
  );
}
